import React from "react";
import { useTheme } from "@/components/ThemeProvider";
import { useOptionalLabel } from "@/providers/LabelProvider";
import { getLabelPlaceholderImageUrl, useLabelCoverImage } from "@/lib/label-placeholders";

interface NoArtworkProps {
  className?: string;
  iconSize?: string;
  showText?: boolean;
  useLabelPlaceholder?: boolean;
  /**
   * The label the TRACK belongs to. Given one, its cover is used as the fallback; without one we
   * fall back to the VIEWER's own label, which is the pre-existing behaviour and is only ever
   * right on a seller's own catalogue (drafts, their own tracks).
   *
   * This is what made the fallback seller-only: a buyer has no label of their own and browses many,
   * so `useOptionalLabel()` gave them nothing and every track showed the default art.
   */
  labelTag?: string | null;
  /**
   * The logo's alt text. With a `labelTag` it defaults to "" (the logo stands in for missing
   * artwork, so it is decorative); without one, to the viewer's label name.
   */
  alt?: string;
}

export function NoArtwork({
  className = "w-full h-full",
  iconSize = "w-6 h-6",
  showText = false,
  useLabelPlaceholder = true,
  labelTag = null,
  alt,
}: NoArtworkProps) {
  const { theme } = useTheme();
  // Optional: this is a presentational placeholder — if it ever renders outside
  // a LabelProvider it should degrade to the default art, not crash the app.
  const label = useOptionalLabel()?.label ?? null;

  const isDarkMode = theme === "dark";

  const backgroundClasses = isDarkMode
    ? "bg-gradient-to-br from-gray-800 to-gray-900"
    : "bg-gradient-to-br from-gray-100 to-gray-200";

  const iconColor = isDarkMode ? "text-gray-500" : "text-gray-400";
  const textColor = isDarkMode ? "text-gray-400" : "text-gray-500";

  // The track's own label wins. When a labelTag is supplied we NEVER fall through to the viewer's
  // label: showing a buyer their own logo — or another company's — on someone else's track would be
  // worse than showing the default art.
  const trackLabelCover = useLabelCoverImage(useLabelPlaceholder ? labelTag : null);
  const labelImageUrl = !useLabelPlaceholder
    ? null
    : labelTag
      ? trackLabelCover
      : getLabelPlaceholderImageUrl(label);

  // If we have a label image, show it cleanly without overlays
  if (labelImageUrl) {
    return (
      <div className={`relative overflow-hidden ${className}`}>
        <img
          src={labelImageUrl}
          alt={alt ?? (labelTag ? "" : label?.name || "Label cover")}
          className="w-full h-full object-cover"
          onError={(e) => {
            // If label image fails, hide it and show the default NoArtwork
            const target = e.target as HTMLImageElement;
            target.style.display = "none";
            const container = target.parentElement;
            if (container) {
              container.innerHTML = `
                <div class="${backgroundClasses} flex items-center justify-center w-full h-full">
                  <div class="flex flex-col items-center justify-center">
                    <svg class="${iconSize} ${iconColor}" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"></path>
                    </svg>
                    ${
                      showText
                        ? `<span class="text-xs mt-1 ${textColor}">No Artwork</span>`
                        : ""
                    }
                  </div>
                </div>
              `;
            }
          }}
        />
      </div>
    );
  }

  // Default NoArtwork component
  return (
    <div
      className={`${backgroundClasses} flex items-center justify-center ${className}`}
    >
      <div className="flex flex-col items-center justify-center">
        <svg
          className={`${iconSize} ${iconColor}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3"
          />
        </svg>
        {showText && (
          <span className={`text-xs mt-1 ${textColor}`}>No Artwork</span>
        )}
      </div>
    </div>
  );
}
